import { DataTable, EmptyState, SectionShell } from '@/dashboard/ui';
// F6a CORRECTIVE (founder tower walk 2026-08-14: this ledger's Date cell
// still printed "2:10 AM IST" on the walked build): the render site now
// reads THE ONE IST owner DIRECTLY -- never through a historic re-export --
// so the "Treatment done - all visits" dates ride the healed one-parse
// formatter (a zone-less value is IST wall-clock, never double-shifted).
import { formatTimestampIst } from '@/lib/datetime-ist';
import {
  treatmentLedgerRows,
  type CaseHistoryLoadState,
  type TreatmentLedgerRow,
} from '@/dashboard/doctor/doctor-consult-view';

/**
 * DOCTOR-OS-EXPERIENCE-V1 (C7/C8) -- THE TREATMENT LEDGER: the paper case
 * sheet's running treatment-done log, digitally, across ALL of this
 * patient's finalized visits. One compact READ-ONLY table below the
 * earlier-visits rail: Date | Treatment done | Paid | Mode | Receipt.
 *
 * DATA: the SAME lifted fetchDoctorCaseHistory read the rail renders from
 * (one fetch, two renders -- the page owns the state). Money renders
 * through THE ONE INR formatter (billing-entry-view.ts, via the pure
 * treatmentLedgerRows derivation); the payment row id is the product's
 * honest receipt identity -- no receipt-number series exists. NO money
 * writer, NO edit affordance anywhere on this surface.
 */

export interface TreatmentLedgerRailProps {
  /** The linked golden-record patient id (null = no ledger yet). */
  readonly patientId: string | null;
  /** The page-lifted case-history read state (shared with the rail). */
  readonly history: CaseHistoryLoadState;
}

export default function TreatmentLedgerRail({
  patientId,
  history,
}: TreatmentLedgerRailProps): JSX.Element | null {
  if (patientId === null) return null;
  return (
    <SectionShell
      eyebrow="Treatment ledger"
      title="Treatment done - all visits"
      
      testId="consult-treatment-ledger"
    >
      {history.kind === 'loading' ? (
        <p className="day-board__loading" role="status">
          Loading treatment ledger...
        </p>
      ) : history.kind === 'error' ? (
        <p className="day-board__error" role="alert">
          {history.text}
        </p>
      ) : (
        <DataTable<TreatmentLedgerRow>
          columns={[
            {
              key: 'date',
              header: 'Date',
              render: (row) => formatTimestampIst(row.dateIso),
            },
            {
              key: 'treatment',
              header: 'Treatment done',
              render: (row) => row.treatmentsDone,
            },
            {
              key: 'paid',
              header: 'Paid',
              align: 'end',
              render: (row) => row.paid,
            },
            { key: 'mode', header: 'Mode', render: (row) => row.modes },
            {
              key: 'receipt',
              header: 'Receipt',
              render: (row) => row.receipts,
            },
          ]}
          rows={treatmentLedgerRows(history.history.entries)}
          rowKey={(row) => row.caseSheetId}
          label="Treatment ledger"
          testId="consult-treatment-ledger-table"
          empty={
            <EmptyState
              title="No finalized visits yet."
              body="Completed treatments and payments accumulate here visit by visit."
              testId="consult-treatment-ledger-empty"
            />
          }
        />
      )}
    </SectionShell>
  );
}
